import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './GlobalSearch.css';

const SOURCES = [
  ['client', 'Clients'], ['appels', 'Appels actifs'], ['archives', 'Appels archivés'], ['planning', 'Planning'],
  ['kanban', 'Kanban'], ['stock', 'Stock'], ['suivi', 'Réparations'],
  ['backup', 'Backups'], ['cdr', 'Lignes CDR'], ['devis', 'Devis Henrri'], ['factures', 'Factures Henrri'],
];
export const normalizeSearch = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export function SearchIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></svg>;
}

export default function GlobalSearch({ open, onClose, apps }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [source, setSource] = useState('all');
  const [responses, setResponses] = useState({});
  const [active, setActive] = useState(0);
  const dialog = useRef(null);
  const input = useRef(null);
  const sources = SOURCES;
  const term = query.trim();

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    setQuery(''); setSource('all'); setActive(0);
    input.current?.focus();
    return () => previous?.focus?.();
  }, [open]);

  useEffect(() => {
    setResponses({});
    if (!open || term.length < 3) return undefined;
    const controller = new AbortController();
    const targets = sources.filter(([key]) => source === 'all' || source === key);
    setResponses(Object.fromEntries(targets.map(([key]) => [key, { loading: true }])));
    const timer = setTimeout(() => {
      targets.forEach(async ([key]) => {
        const request = new AbortController();
        const timeout = setTimeout(() => request.abort(), 20000);
        const cancel = () => request.abort();
        controller.signal.addEventListener('abort', cancel);
        try {
          const params = new URLSearchParams({ q: term, source: key });
          const response = await fetch(`https://${key === 'cdr' ? 'cdr' : 'cms'}.amservices.eu/api/global_search.php?${params}`, {
            signal: request.signal,
            ...(key === 'cdr' ? { credentials: 'include' } : { headers: { Authorization: `Bearer ${localStorage.getItem('am_token') || ''}` } }),
          });
          const data = await response.json();
          if (key === 'cdr' && response.status === 401) throw new Error('Ouvrez CDR pour activer sa session, puis relancez la recherche.');
          if (!response.ok || !data.success) throw new Error(data.message || 'Source indisponible.');
          if (!controller.signal.aborted) setResponses(current => ({ ...current, [key]: { items: data.items || [], more: data.more } }));
        } catch (error) {
          if (!controller.signal.aborted) setResponses(current => ({ ...current, [key]: { error: error.name === 'AbortError' ? 'Délai de réponse dépassé.' : error.message } }));
        } finally {
          clearTimeout(timeout);
          controller.signal.removeEventListener('abort', cancel);
        }
      });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [open, term, sources, source]);

  const appResults = source === 'all' ? apps.filter(app => normalizeSearch(app.label).includes(normalizeSearch(term))) : [];
  const results = [
    ...appResults.map(app => ({ id: `app-${app.key}`, title: app.label, description: 'Ouvrir l’application', path: app.to, group: 'Applications' })),
    ...sources.flatMap(([key, label]) => (responses[key]?.items || []).map(item => ({ ...item, id: `${key}-${item.id}`, group: label }))),
  ];
  const pending = Object.values(responses).some(response => response.loading);
  const activeIndex = Math.min(active, Math.max(0, results.length - 1));
  useEffect(() => { setActive(0); }, [term, source]);
  useEffect(() => {
    document.getElementById(`global-result-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const select = item => {
    // Routes issues uniquement des applications autorisées du CRM.
    if (!apps.some(app => app.to === item.path)) return;
    navigate(item.path, { state: item.query ? { globalSearch: { query: item.query, id: item.id.split('-').slice(1).join('-'), nonce: Date.now(), view: item.view, date: item.date, documentId: item.documentId, documentType: item.documentType } } : null });
    onClose();
  };
  const onKeyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    if (event.target === input.current && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      setActive((activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + results.length) % (results.length || 1));
    }
    if (event.target === input.current && event.key === 'Enter' && !event.nativeEvent.isComposing && results[activeIndex]) {
      event.preventDefault(); select(results[activeIndex]);
    }
    if (event.key === 'Tab') {
      const elements = [...dialog.current.querySelectorAll('button, input, select')].filter(element => !element.disabled);
      const first = elements[0]; const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  };

  if (!open) return null;
  return createPortal(
    <div className="global-search-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="global-search-dialog" role="dialog" aria-modal="true" aria-labelledby="global-search-title" ref={dialog} onKeyDown={onKeyDown}>
        <header><span id="global-search-title">Recherche globale</span><button onClick={onClose} aria-label="Fermer la recherche">Échap</button></header>
        <div className="global-search-input"><SearchIcon /><input ref={input} value={query} onChange={event => setQuery(event.target.value)} maxLength={100} placeholder="Client, téléphone, tâche, référence…" aria-label="Rechercher dans le CRM" role="combobox" aria-expanded="true" aria-autocomplete="list" aria-controls="global-search-results" aria-activedescendant={results.length ? `global-result-${activeIndex}` : undefined} /></div>
        <div className="global-search-toolbar"><label>Rechercher dans <select value={source} onChange={event => setSource(event.target.value)}><option value="all">Toutes les applications</option>{sources.map(([key, label]) => <option value={key} key={key}>{label}</option>)}</select></label><span role="status">{pending ? 'Recherche en cours…' : `${results.length} résultat${results.length > 1 ? 's' : ''}`}</span></div>
        <div className="global-search-results" id="global-search-results" role="listbox" aria-label="Résultats">
          {results.map((item, index) => <React.Fragment key={item.id}>
            {(index === 0 || results[index - 1].group !== item.group) && <div className="global-search-group" role="presentation">{item.group}</div>}
            <div id={`global-result-${index}`} role="option" aria-selected={index === activeIndex} className={`global-search-result ${index === activeIndex ? 'selected' : ''}`} onMouseMove={() => setActive(index)} onClick={() => select(item)}><span><strong>{item.title}</strong><small>{item.description}</small></span><span aria-hidden="true">↗</span></div>
          </React.Fragment>)}
          {!results.length && <p className="global-search-empty">{term.length < 3 ? 'Saisissez au moins 3 caractères pour chercher dans vos données.' : pending ? 'Recherche dans vos applications…' : 'Aucun résultat dans les sources disponibles.'}</p>}
        </div>
        <div className="global-search-messages" aria-live="polite">
          {term.length > 0 && term.length < 3 && <p>À partir de 3 caractères : recherche dans les données.</p>}
          {sources.filter(([key]) => responses[key]?.error).map(([key, label]) => <p key={key}>{label} : {responses[key].error}</p>)}
          {sources.filter(([key]) => responses[key]?.more).map(([key, label]) => <p key={key}>{label} : 12 résultats affichés, précisez votre recherche.</p>)}
        </div>
        <footer><span>↑ ↓ Parcourir · Entrée Ouvrir</span><span>Ctrl K</span></footer>
      </section>
    </div>, document.body,
  );
}
